import { defineStore } from 'pinia'

export const useMenuStore = defineStore('menu', {
    state: () => ({
        menuItems: [],
        loading: false,
        error: null,
        subscriptionError: null, // 訂閱相關錯誤（過期、寬限期等）
    }),

    getters: {
        formattedMenu: (state) => {
            if (!state.menuItems || state.menuItems.length === 0) return []

            return state.menuItems.map((module) => ({
                id: module.id,
                code: module.code,
                name: module.name,
                icon: module.icon || 'ri-folder-line',
                category: module.category,
                children: (module.programs || []).map((program) => ({
                    id: program.id,
                    code: program.code,
                    name: program.name,
                    path: program.path,
                    icon: program.icon || 'ri-file-line',
                    permissions: program.permissions || [],
                })),
            }))
        },
    },

    actions: {
        async fetchMenu(forceRefresh = false) {
            // 如果已經有資料且不是強制刷新，直接返回快取的資料
            if (!forceRefresh && this.menuItems && this.menuItems.length > 0) {
                return { success: true, data: this.menuItems };
            }

            this.loading = true
            this.error = null

            try {
                const { menuService } = await import('@/api/modules/menu')
                const response = await menuService.getMenu()

                // 後端回應格式：{ success: true, data: { modules: [...] } }
                // axios interceptor 已經返回 response.data，所以這裡直接是後端的回應格式

                // 檢查回應格式
                if (!response) {
                    this.error = '獲取選單失敗：回應為空'
                    this.menuItems = []
                    return { success: false, error: '獲取選單失敗：回應為空' }
                }

                // 處理成功回應
                if (response.success === true) {
                    const modules = (response.data && response.data.modules) || []

                    // 檢查是否有訂閱相關錯誤（過期、寬限期等）
                    if (response.data && response.data.error) {
                        this.subscriptionError = response.data.error
                    } else {
                        this.subscriptionError = null
                    }

                    // 轉換數據格式：將 modules 和 programs 轉換為 menuItems
                    this.menuItems = modules.map(module => ({
                        id: module.id,
                        code: module.code,
                        name: module.name,
                        icon: module.icon || 'ri-folder-line',
                        category: module.category,
                        programs: module.programs || []
                    }))

                    // 即使沒有模組，也返回成功（資料庫可能是空的）
                    this.error = null
                    return { success: true, data: this.menuItems }
                }

                // 處理錯誤回應
                if (response.success === false) {
                    const errorMsg = (response && response.message) || (response && response.error && response.error.message) || '獲取選單失敗'
                    this.error = errorMsg
                    this.menuItems = []
                    return { success: false, error: errorMsg }
                }

                // 回應格式不符合預期，但嘗試解析
                // 嘗試從不同位置獲取 modules
                let modules = []
                if (response.data && response.data.modules) {
                    modules = response.data.modules
                } else if (response.modules) {
                    modules = response.modules
                } else if (Array.isArray(response.data)) {
                    modules = response.data
                } else if (Array.isArray(response)) {
                    modules = response
                }

                if (modules.length > 0 || Array.isArray(modules)) {
                    this.menuItems = modules.map(module => ({
                        id: module.id,
                        code: module.code,
                        name: module.name,
                        icon: module.icon || 'ri-folder-line',
                        category: module.category,
                        programs: module.programs || []
                    }))
                    this.error = null
                    return { success: true, data: this.menuItems }
                }

                // 無法解析回應
                this.error = null // 不顯示錯誤，可能是資料庫中沒有資料
                this.menuItems = []
                return { success: true, data: [] } // 返回空陣列，不拋出錯誤

            } catch (error) {
                // 不拋出錯誤，只設置錯誤狀態
                const errorMessage = (error.response && error.response && error.response.data && error.response.data.message) || error.message || '獲取選單失敗'
                this.error = errorMessage
                this.menuItems = []
                // 不重新拋出錯誤，只返回錯誤狀態
                return { success: false, error: errorMessage }
            } finally {
                this.loading = false
            }
        },

        clearMenu() {
            this.menuItems = []
            this.error = null
            this.subscriptionError = null
        },
    },
})
